import React, { useEffect, useMemo, useRef } from "react";

import {
  YOUTUBE_PLAYER_INIT_TIMEOUT_MS,
  YOUTUBE_PLAYER_STATE,
  buildYoutubePlayerPageUrl,
  parseYoutubePlayerPageMessage,
} from "./logics/youtubePlayerPage";

/**
 * エラー要因の区分。
 * - load: 中継ページ・IFrame API のロード失敗（通信不良など。再試行で回復し得る）
 * - playback: YT.Player の onError イベント（コード 2/5/100/101/150/153 など）。
 *   回復可能かどうかはエラーコード次第のため、扱いの分岐は親側（detail.tsx の handleYoutubeError）が行う
 */
export type YoutubePlayerErrorCause = "load" | "playback";

type YoutubePlayerProps = {
  videoId: string;
  className?: string;
  /** 再生開始・再開（PlayerState.PLAYING）。<video> の onPlay 相当 */
  onPlay: () => void;
  /** 再生中の現在位置通知。<video> の onTimeUpdate 相当 */
  onProgress: (currentTimeSeconds: number) => void;
  /** 一時停止（PlayerState.PAUSED）。<video> の onPause 相当 */
  onPause: (currentTimeSeconds: number) => void;
  /** 再生終了（PlayerState.ENDED）。<video> の onEnded 相当 */
  onEnded: (currentTimeSeconds: number, durationSeconds?: number) => void;
  /** 再生中かどうかの変化通知（離脱時の視聴時間積算の判定に使う） */
  onPlayingChange?: (isPlaying: boolean) => void;
  /**
   * ロード失敗・再生不可の通知。親側でエラー表示・再試行導線を出すために使う。
   * playback 時は YT のエラーコードを第2引数で渡す（回復可能かの判定は親側で行う）
   */
  onError?: (cause: YoutubePlayerErrorCause, errorCode?: number) => void;
};

/** 動画IDが不正で中継ページを開けない場合に使う YT エラーコード（動画が見つからない） */
const INVALID_VIDEO_ID_ERROR_CODE = 100;

/**
 * ウェビナー動画の YouTube プレイヤー。
 *
 * YouTube を直接 iframe で埋め込むと、アプリ（capacitor:// スキーム）の WebView からは Referer が
 * 送られず YouTube 側に再生を拒否されるため、http(s) オリジンに置いた中継ページ
 * （public/youtube_player.html）を iframe で読み込み、再生状態を postMessage で受け取る。
 * - フルスクリーンは YouTube 標準コントロールに任せる（自前制御はしない）
 * - 現在位置のポーリングは中継ページ側で行い、ここでは progress メッセージを onProgress に流すだけ
 */
export const YoutubePlayer: React.FC<YoutubePlayerProps> = ({
  videoId,
  className,
  onPlay,
  onProgress,
  onPause,
  onEnded,
  onPlayingChange,
  onError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const handlersRef = useRef({ onPlay, onProgress, onPause, onEnded, onPlayingChange, onError });
  handlersRef.current = { onPlay, onProgress, onPause, onEnded, onPlayingChange, onError };

  const playerPageUrl = useMemo(() => buildYoutubePlayerPageUrl(videoId), [videoId]);

  useEffect(() => {
    if (!playerPageUrl) {
      // API 側でバリデーション済みのため通常は起きない。再試行しても回復しないコードで親へ通知する
      handlersRef.current.onError?.("playback", INVALID_VIDEO_ID_ERROR_CODE);
      return undefined;
    }

    const playerPageOrigin = new URL(playerPageUrl).origin;

    // 中継ページ（または YouTube の iframe）だけが遮断されて ready / state / error のどれも来ない
    // ケース（広告ブロッカー・企業ネットワーク等）を検知する watchdog。
    // 最初のメッセージ・アンマウントのいずれかで解除する。
    // 誤発火しても load 扱い（再試行＋「YouTubeで見る」導線）なので回復可能。
    // バックグラウンド（hidden）中はタイマーも iframe ロードも止まるため、hidden を経た計測窓の
    // 期限切れは iframe 遮断の根拠にならない。モバイル WebView では停止したタイマーがフォアグラウンド
    // 復帰後（visibilityState が既に "visible" になった後）に遅延発火するため、発火時点の可視状態
    // だけでは判定できない。そこで計測窓の間に一度でも hidden になったか（発火時点で hidden の場合を
    // 含む）を記録し、該当する発火では onError せず 1 回だけ再アーム（フラグをリセットしてタイマー
    // 再スタート）して、可視状態での計測時間をあらためて確保する。再アームはこの発火時の 1 経路に
    // 限定し、hidden/visible の短周期トグルで際限なく再アームし続けないようにする（再アーム後の窓で
    // 再び hidden になれば、その窓の発火時にまた 1 回だけ再アームされるのは許容）
    let watchdogTimerId: number | null = null;
    let wasHiddenDuringWatchdogWindow = false;
    const recordHiddenDuringWatchdogWindow = () => {
      if (document.visibilityState === "hidden") {
        wasHiddenDuringWatchdogWindow = true;
      }
    };
    const clearWatchdog = () => {
      if (watchdogTimerId !== null) {
        window.clearTimeout(watchdogTimerId);
        watchdogTimerId = null;
      }
      document.removeEventListener("visibilitychange", recordHiddenDuringWatchdogWindow);
    };
    const armWatchdogTimer = () => {
      wasHiddenDuringWatchdogWindow = false;
      watchdogTimerId = window.setTimeout(() => {
        watchdogTimerId = null;
        if (wasHiddenDuringWatchdogWindow || document.visibilityState === "hidden") {
          // 「あと数秒で ready が来る」正常なプレイヤーを誤ってエラー表示にしないよう、可視状態の計測窓で判定をやり直す
          armWatchdogTimer();
          return;
        }
        document.removeEventListener("visibilitychange", recordHiddenDuringWatchdogWindow);
        // iframe 遮断は広告ブロッカー等で高頻度に起き得るため、CaptureConsole（error/warn）で
        // Sentry へ送られるレベルでは出さない（load 失敗経路と同じ扱い）
        // eslint-disable-next-line no-console
        console.info("YouTube プレイヤーの読み込みを検知できませんでした");
        handlersRef.current.onError?.("load");
      }, YOUTUBE_PLAYER_INIT_TIMEOUT_MS);
    };
    document.addEventListener("visibilitychange", recordHiddenDuringWatchdogWindow);
    armWatchdogTimer();

    const handleMessage = (event: MessageEvent) => {
      // 中継ページ以外（GTM・YouTube 本体・他の iframe）からのメッセージを取り込まないよう、
      // 発信元のウィンドウとオリジン、メッセージの識別子の 3 点で絞り込む
      // MessageEventSource は MessagePort 系も含むユニオン型のため、Window として比較する
      const messageSource = event.source as unknown as Window | null;
      if (messageSource !== iframeRef.current?.contentWindow || event.origin !== playerPageOrigin) {
        return;
      }
      const message = parseYoutubePlayerPageMessage(event.data);
      if (!message) {
        return;
      }
      clearWatchdog();

      switch (message.type) {
        case "ready":
          break;
        case "progress":
          handlersRef.current.onProgress(message.currentTime);
          break;
        case "state":
          if (message.state === YOUTUBE_PLAYER_STATE.PLAYING) {
            handlersRef.current.onPlayingChange?.(true);
            handlersRef.current.onPlay();
            break;
          }
          handlersRef.current.onPlayingChange?.(false);
          if (message.state === YOUTUBE_PLAYER_STATE.PAUSED) {
            handlersRef.current.onPause(message.currentTime);
          } else if (message.state === YOUTUBE_PLAYER_STATE.ENDED) {
            handlersRef.current.onEnded(message.currentTime, message.duration);
          }
          break;
        case "error":
          // 埋め込み禁止（101/150）・動画が見つからない（100）・埋め込み元を識別できない（153）など。
          // 黒画面のまま放置しないよう親へ通知する。
          // console.error（CaptureConsole 経由の Sentry 送信）は webinar id を持つ親側で 1 回だけ行う
          handlersRef.current.onPlayingChange?.(false);
          handlersRef.current.onError?.("playback", message.code);
          break;
        case "load_error":
          // 通信不良・広告ブロッカーで高頻度に起き得るため、Sentry へ送られるレベルでは出さない
          handlersRef.current.onPlayingChange?.(false);
          handlersRef.current.onError?.("load");
          break;
      }
    };
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearWatchdog();
      handlersRef.current.onPlayingChange?.(false);
    };
  }, [playerPageUrl]);

  if (!playerPageUrl) {
    return null;
  }

  return (
    <iframe
      ref={iframeRef}
      className={className}
      src={playerPageUrl}
      title="ウェビナー動画"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      data-testid="youtube-player-frame"
    />
  );
};
